<template>
  <div class="form-list">
    <el-form
      ref="form"
      :model="card"
      label-position="right"
      label-width="160px"
      class="form"
      :rules="rules"
    >
      <div class="table-title table-title-top">申请信息</div>
      <el-form-item :label="`${card.update_time ? '初次' : ''}申请时间`">{{ card.add_time | timeToYmdHi }}</el-form-item>

      <el-form-item v-if="card.update_time" label="重新申请时间">{{ card.update_time | timeToYmdHi }}</el-form-item>

      <el-form-item label="申请者">
        <v-avatar-nickname
          :value="card.admin_avatar"
          :nickname="card.admin_nickname"
          :is-center="false"
        />
      </el-form-item>

      <el-form-item label="申请门店">{{ card.store_name }}</el-form-item>

      <el-form-item label="申请办卡日期" prop="register_time">{{ card.register_time | timeToYmd }}</el-form-item>

      <el-form-item label="申请自动开卡日期" prop="start_time_auto">{{ card.start_time_auto | timeToYmdHi }}</el-form-item>

      <el-form-item label="申请原因">
        <span v-if="card.apply_remark" class="danger-color">{{ card.apply_remark }}</span>
        <span v-else class="info-color">有效次数、有效天数、实际付款与默认相符，无需填写</span>
      </el-form-item>

      <div class="table-title">课卡信息</div>

      <div class="field-group-label">基础信息</div>

      <el-form-item label="所属学员">
        <v-avatar-nickname
          :value="card.selfie"
          :nickname="card.student_name"
          :link-page-name="RouteConfig.studentDetail.name"
          :link-page-id="card.student_id"
          :avatar-size="50"
        />
      </el-form-item>

      <el-form-item label="会员卡号">
        <span v-if="card.card_number">{{ card.card_number }}</span>
        <span v-else class="info-color">已有卡号，无需填写</span>
      </el-form-item>

      <el-form-item label="课卡类型">{{ card.card_type_name }}</el-form-item>

      <el-form-item label="课卡名称">{{ card.card_name }}</el-form-item>

      <el-form-item label="禁止预约专栏">
        <span v-if="card.is_disable_column" class="danger-color">是</span>
        <span v-else class="success-color">否</span>
      </el-form-item>

      <el-form-item label="可用日期">
        <span v-if="allWeeksAvailable" class="success-color">无限制</span>
        <span v-else v-for="(v, k) in card.enabled_weeks" :key="v.value">
          {{ v.label }}{{k === card.enabled_weeks.length - 1 ? '' : '、'}}
        </span>
      </el-form-item>

      <el-form-item label="是否设为默认支付课卡">
        <span v-if="card.is_default_used" class="success-color">是</span>
        <span v-else class="danger-color">否</span>
      </el-form-item>

      <div class="field-group-label">使用规则</div>

      <el-form-item label="适用门店">
        <template v-if="card.store_names && card.store_names.length">
          <span v-for="(v, index) in card.store_names" :key="v">{{ index ? '、' : ''}}{{ v }}</span>
        </template>
        <span v-else class="success-color">全国通用</span>
      </el-form-item>

      <template v-if="card.store_ids && card.store_ids.length">
        <el-form-item label="适用门店限制天数" prop="store_limit_expire_days">
          {{ card.store_limit_expire_days }} 天
        </el-form-item>
      </template>

      <el-form-item label="课卡默认有效天数">
        <span class="danger-color">{{ card.expire_days_default }}</span> 天
      </el-form-item>

      <el-form-item label="实际办理有效天数">
        <span class="success-color">{{ card.expire_days }}</span> 天
      </el-form-item>

      <el-form-item label="课卡默认有效次数">
        <template v-if="card.is_number_limit">
          <span class="danger-color">{{ card.expire_number_default | toYuan }}</span> 次
        </template>
        <span v-else class="success-color">期限卡，无次数限制</span>
      </el-form-item>

      <el-form-item label="实际办理有效次数">
        <template v-if="card.is_number_limit">
          <span class="success-color">{{ card.expire_number | toYuan }}</span> 次
        </template>
        <span v-else class="success-color">期限卡，无次数限制</span>
      </el-form-item>

      <div class="field-group-label">支付信息</div>

      <el-form-item label="收款方式">
        <span v-if="card.actual_payment > 0">{{ findPaymentModeName(card.card_payment_mode_id) }}</span>
        <span v-else class="success-color">免费赠送</span>
      </el-form-item>

      <template v-if="card.card_payment_mode_id === cardPaymentModeIds.youZan">
        <el-form-item label="有赞券码">{{ card.you_zan_number || '' }}</el-form-item>
        <el-form-item label="有赞核销金额">{{ card.you_zan_payment | toYuan }} 元</el-form-item>
      </template>

      <el-form-item label="课卡原价">
        <span class="danger-color">{{ card.price | toYuan }}</span> 元
      </el-form-item>

      <el-form-item label="积分抵扣">
        <span class="success-color">{{ card.integral | toYuan }}</span> 元
      </el-form-item>

      <el-form-item label="代金券抵扣">
        <template v-if="card.vouchers && card.vouchers.length">
          <div v-for="voucher in card.vouchers" :key="voucher.voucher_id" class="voucher-line">
            <router-link :to="RouteConfig.voucherDetail.to(voucher.voucher_id)" class="primary-color">{{ voucher.voucher_type_name }}</router-link>
            <span class="success-color"> {{ voucher.amount_in_cents | toYuan }} 元</span>
          </div>
          <div v-if="card.vouchers.length > 1" class="voucher-total">
            合计：<span class="success-color voucher-total-amount">{{ card.voucher_amount_in_cents | toYuan }}</span> 元
          </div>
        </template>
        <span v-else class="info-color">未使用代金券</span>
      </el-form-item>

      <el-form-item label="实际付款">
        <span class="income-color">{{ card.actual_payment | toYuan }}</span> 元
      </el-form-item>

      <el-form-item label="付款凭证">
        <v-img v-if="card.receipt" :value="card.receipt" class="cover"/>
        <span v-else class="info-color">无</span>
      </el-form-item>

      <el-form-item label="付款备注">
        <span v-if="card.payment_remark" class="warning-color">{{ card.payment_remark }}</span>
        <span v-else class="info-color">无</span>
      </el-form-item>

      <el-form-item label="备注文件">
        <v-remark-file-list v-if="card.remark_files && card.remark_files.length" :files="card.remark_files"/>
        <span v-else class="info-color">未上传</span>
      </el-form-item>

      <template v-if="card.card_ticket_type_id">
        <div class="table-title">赠券信息</div>

        <el-form-item label="课次券名称">
          <span class="success-color">{{ card.card_ticket_type_name }}</span>
        </el-form-item>
        <el-form-item label="有效次数">
          <span class="success-color">{{ card.card_ticket_expire_number | toYuan }}</span> 次
        </el-form-item>
        <el-form-item label="有效天数">
          <span class="success-color">{{ card.expire_days }}</span> 天
        </el-form-item>
      </template>

      <div class="table-title">审核信息</div>

      <el-form-item label="审核状态">
        <span v-if="!isLoading && !isLoadingError" :class="`${applyState.tagType}-color`">{{ applyState.name }}</span>
      </el-form-item>

      <template v-if="!isLoading && !isLoadingError && applyState.timeLabel && canViewReviewInfo">
        <el-form-item :label="applyState.timeLabel">{{ card.update_time | timeToYmdHi }}</el-form-item>
        <el-form-item :label="applyState.personLabel">
          <v-avatar-nickname
            :value="card.admin_avatar_update"
            :nickname="card.admin_nickname_update"
            :is-center="false"
          />
        </el-form-item>
      </template>

      <el-form-item v-if="applyState.value === applyStates.rejected.value && canViewReviewInfo" label="驳回原因">
        <span class="danger-color">{{ card.reject_reason || '' }}</span>
      </el-form-item>

      <el-form-item v-if="applyState.value === applyStates.passed.value && isAdministrator" label="审核备注">
        <span v-if="card.pass_remark" class="danger-color">{{ card.pass_remark }}</span>
        <span v-else class="info-color">未填写</span>
      </el-form-item>

      <template v-if="!isLoading && !isLoadingError && card.apply_state === applyStates.review.value">
        <template v-if="isAdministrator">
          <el-form-item label="审核结果">
            <el-radio v-model="isReject" :label="0" :disabled="isExamining">通过</el-radio>
            <el-radio v-model="isReject" :label="1" :disabled="isExamining">驳回</el-radio>
          </el-form-item>

          <el-form-item v-if="isReject" label="驳回原因" prop="reject_reason">
            <el-input
              class="clearable-word-limit-input"
              v-model="card.reject_reason"
              maxlength="128"
              placeholder="请输入驳回原因"
              show-word-limit
              clearable
              :disabled="isExamining"
            />
          </el-form-item>
          <el-form-item v-else label="审核备注" prop="pass_remark">
            <el-input
              class="clearable-word-limit-input"
              v-model="card.pass_remark"
              maxlength="128"
              placeholder="请输入审核备注"
              show-word-limit
              clearable
              :disabled="isExamining"
            />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="handleExamine" :loading="isExamining">完成审核</el-button>
            <el-button @click="RouteConfig.cardAddApplyList.back">返回</el-button>
          </el-form-item>
        </template>
        <el-form-item v-else>
          <el-button type="danger" @click="handleRevoke" :loading="isExamining">撤回</el-button>
          <el-button @click="RouteConfig.cardAddApplyList.back">返回</el-button>
        </el-form-item>
      </template>
      <el-form-item v-else>
        <v-list-button v-if="card.apply_state === applyStates.rejected.value || card.apply_state === applyStates.revoked.value" icon="edit" name="修改" :to="RouteConfig.cardAddApplyEdit.to(id)"/>
        <v-list-button icon="return" name="返回" @click="RouteConfig.cardAddApplyList.back"/>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import RouteConfig from '@/router/config';
import cardPaymentModeIds from '@/enums/cardPaymentModeIds';
import weeks from "@/enums/weeks";
import { applyStates, findApplyState } from "@/enums/applyStates";
import { show, update, pass, destroy } from "@/api/card-add-apply";
import { findPaymentModeName } from "@/enums/paymentModes";
import { mapGetters } from 'vuex';

export default {
  name: RouteConfig.cardAddApplyDetail.name,
  data: () => ({
    RouteConfig,

    isLoading: false,
    isLoadingError: false,
    isExamining: false,
    isReject: 0,
    weeks: JSON.parse(JSON.stringify(weeks)),

    applyStates,
    cardPaymentModeIds,
    card: {
      add_time: 0,
      update_time: 0,
      student_id: 0,
      student_name: '',
      card_number: '',
      selfie: '',
      store_names: [],
      is_generic: 0,
      card_type_id: 0,
      card_type_name: '',
      card_name: '',
      card_charge_type_id: '',
      card_payment_mode_name: '',
      is_number_limit: 0,
      expire_number: '',
      expire_number_default: '',
      expire_days: '',
      expire_days_default: '',
      store_limit_expire_days: '',
      card_payment_mode_id: '',
      you_zan_number: '',
      price: 0,
      actual_payment: '',
      you_zan_payment: '',
      integral: 0,
      vouchers: [],
      voucher_amount_in_cents: 0,
      payment_remark: '',
      receipt: '',
      remark_files: [],
      register_time: '',
      start_time_auto: '',
      update_remark: '',
      update_payment: '',
      is_reviewing: 0,
      enabled_weeks: [],
      apply_state: applyStates.review.value,
      apply_remark: '',
      admin_avatar: '',
      admin_nickname: '',
      admin_avatar_update: '',
      admin_nickname_update: '',
      store_name: '',
      is_default_used: 0,
      is_disable_column: 0,
      reject_reason: '',
      pass_remark: '',
      card_ticket_type_id: 0,
      card_ticket_expire_number: 0,
      card_ticket_expire_time: 0,
      card_ticket_type_name: '',
      store_id: 0,
    },
    rules: {
      reject_reason: [{ required: true, message: '请输入 1 ~ 128 字符的驳回原因', trigger: 'change' }],
    },
  }),
  computed: {
    ...mapGetters(['isAdministrator', 'userStoreId']),
    canViewReviewInfo() {
      return this.isAdministrator || this.userStoreId === this.card.store_id;
    },
    id() {
      return +this.$route.params.id || 0;
    },
    applyState() {
      return findApplyState(this.card.apply_state)[0] || {};
    },
    allWeeksAvailable() {
      return this.card.enabled_weeks.length === this.weeks.length;
    },
  },
  beforeMount() {
    this.show();
  },
  methods: {
    findPaymentModeName,
    async show() {
      this.isLoadFailed = false;
      const { isError, data } = await show(this.id);
      if (isError || !data) return this.isLoadFailed = true;

      const { card } = this;
      let updateData = {};
      let enabledWeeks = [];

      Object.keys(data).forEach(key => {
        if (key in card) updateData[key] = data[key];
      });

      if (Array.isArray(data.disabled_weeks) && data.disabled_weeks.length) {
        const { weeks } = this;
        for (let i = 0; i < weeks.length; i++) {
          if (!data.disabled_weeks.includes(weeks[i].value)) enabledWeeks.push(weeks[i]);
        }
      } else {
        enabledWeeks = weeks;
      }

      updateData.enabled_weeks = enabledWeeks;
      Object.assign(card, updateData);
    },
    handleExamine() {
      if (this.isExamining) return;

      this.$refs.form.validate(async (isSuccess) => {
        if (!isSuccess) return;

        this.isExamining = true;
        this.isReject ? this.handleReject() : this.handlePass();
      });
    },
    async handlePass() {
      const { isError } = await pass(this.id, { pass_remark: this.card.pass_remark, update_time: this.card.update_time });
      this.isExamining = false;

      if (!isError) RouteConfig.cardAddApplyList.push({ apply_state: applyStates.review.value });
    },
    async handleReject() {
      const { isError } = await update(
        this.id,
        { reject_reason: this.card.reject_reason, update_time: this.card.update_time },
      );
      this.isExamining = false;

      if (!isError) RouteConfig.cardAddApplyList.push({ apply_state: applyStates.review.value });
    },
    async handleRevoke() {
      if (this.isExamining) return;

      this.isExamining = true;
      const { isError } = await destroy(this.id, this.card.update_time);
      if (isError) return;

      RouteConfig.cardAddApplyList.push({ apply_state: applyStates.revoked.value });
    },
  },
};
</script>
<style src="./card-detail.css" scoped/>
